import { useEffect, useMemo } from "react";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { DefaultFormRadioGroupInput } from "../../../../../../common/components/default_form_radio_group_input";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { IconButton, IconContainerProps, Rating, styled } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import FormContainer from "../../../../../../common/components/form_container";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";
import { EntregaPesquisaSatisfacaoForm, EntregaPesquisaSatisfacaoSchema } from "../../../schemas/entrega_obra_cliente_verificacao/entrega_pesquisa_satisfacacao";
import { EntregaObraPesquisaSatisfacao } from "../../../../domain/models/entrega_obra_pesquisa_satisfacao";
import { EntregaObraClienteChecklist } from "../../../../domain/models/entrega_obra_cliente_checklist";
import { EntregaObrasTopHeader } from "./entrega_obra_top_header";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * Icons
 */

const customIcons: {
  [index: string]: {
    textColor: string;
    icon: React.ReactElement<unknown>;
    label: string;
  };
} = {
  1: {
    textColor: 'text-red-500',
    icon: <SentimentVeryDissatisfiedIcon className="text-red-500" />,
    label: 'Very Dissatisfied',
  },
  2: {
    textColor: 'text-orange-500',
    icon: <SentimentDissatisfiedIcon className="text-orange-500" />,
    label: 'Dissatisfied',
  },
  3: {
    textColor: 'text-yellow-500',
    icon: <SentimentSatisfiedIcon className="text-yellow-500" />,
    label: 'Neutral',
  },
  4: {
    textColor: 'text-green-300',
    icon: <SentimentSatisfiedAltIcon className="text-green-300" />,
    label: 'Satisfied',
  },
  5: {
    textColor: 'text-green-600',
    icon: <SentimentVerySatisfiedIcon className="text-green-600" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  const isSelected = other.className?.includes('MuiRating-iconFilled');
  return (
    <span {...other}>
      <div className="flex flex-col items-center">
        <span className="flex">
          {customIcons[value].icon}
        </span>
        <span className={`${isSelected ? customIcons[value].textColor : ''} text-xs`}>
          {typeof value === 'number' && value}
        </span>
      </div >
    </span >
  );
}

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

/**
 * GenerateColumns
 */

type GenerateColumnsProps = {
  onActionNotaChange: (idItemPesquisaCliente: number, nota: number | null) => void;
  onActionNotaClear: (idItemPesquisaCliente: number) => void;
};

const GenerateColumns = ({
  onActionNotaChange,
  onActionNotaClear,
}: GenerateColumnsProps): GridColDef[] => {
  return [
    {
      field: "descricao",
      filterable: false,
      headerName: "Habilidade",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "nota",
      filterable: false,
      headerName: "Nota",
      width: 200,
      sortable: false,
      align: 'center',
      valueGetter: (_, rows) => {
        return [rows['idItemPesquisaEntregaObra'] as number, rows['nota'] as number];
      },
      renderCell: ({ value }) => {
        return (
          <StyledRating
            className="w-full h-full"
            getLabelText={(value: number) => customIcons[value].label}
            highlightSelectedOnly={true}
            value={value[1]}
            slotProps={{
              root: {
                className: "flex flex-row justify-center items-center gap-2",
              },
              icon: {
                component: IconContainer,
              }
            }}
            onChange={(_, newNota) => { onActionNotaChange(value[0], newNota); }}
          />
        );
      },
    },
    {
      field: "acoes",
      filterable: false,
      headerName: "Ações",
      sortable: false,
      width: 60,
      valueGetter: (_, rows) => rows['idItemPesquisaEntregaObra'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => { onActionNotaClear(value); }}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-orange-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}

/**
 * PesquisaSatisfacaoClienteModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedEntregaObraClienteChecklist: EntregaObraClienteChecklist | null;
  selectedPesquisaSatisfacaoEntrega: EntregaObraPesquisaSatisfacao | null;
  onClose: () => void;
  onSubmit: (form: EntregaPesquisaSatisfacaoForm) => void;
};

export const PesquisaSatisfacaoEntregaModal = ({
  open,
  saving,
  loading,
  selectedEntregaObraClienteChecklist,
  selectedPesquisaSatisfacaoEntrega,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<EntregaPesquisaSatisfacaoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(EntregaPesquisaSatisfacaoSchema),
  });

  const [itens] = hookForm.watch(['itens'])

  const percPonto = useMemo(() => {
    if (itens === undefined || itens === null || itens.length === 0) return 0;
    const reduceFunc = (acc: number, x: any) => x.nota !== null ? acc + x.nota : acc;
    return Math.round((itens.reduce(reduceFunc, 0) / (itens.length * 5)) * 100) / 100;
  }, [
    itens,
  ]);

  const columnDefinition = useMemo(() => {
    return GenerateColumns({
      onActionNotaChange: (idItemPesquisaEntregaObra: number, nota: number | null) => {
        const items = hookForm.getValues('itens');
        const itemIndex = items.findIndex(i => i.idItemPesquisaEntregaObra === idItemPesquisaEntregaObra);
        if (itemIndex !== -1) {
          items[itemIndex].nota = nota;
          hookForm.setValue('itens', items);
        }
      },
      onActionNotaClear: (idItemPesquisaEntregaObra: number) => {
        const items = hookForm.getValues('itens');
        const itemIndex = items.findIndex(i => i.idItemPesquisaEntregaObra === idItemPesquisaEntregaObra);
        if (itemIndex !== -1) {
          items[itemIndex].nota = null;
          hookForm.setValue('itens', items);
        }
      },
    });
  }, [
    hookForm,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Em Aberto',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Finalizada',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedPesquisaSatisfacaoEntrega) {
        const {
          idEntregaPesquisa,
          idEntregaObraCliente,
          data,
          status,
          observacao,
          itens
        } = selectedPesquisaSatisfacaoEntrega;

        hookForm.setValue('idEntregaPesquisa', idEntregaPesquisa);
        hookForm.setValue('idEntregaObraCliente', idEntregaObraCliente ?? null);
        hookForm.setValue('data', data ? dayjs.utc(data).local().toDate() : null);
        hookForm.setValue('observacao', observacao ?? null);
        hookForm.setValue('status', status ?? 0);
        hookForm.setValue('itens', (itens || []).map(i => ({
          idItemPesquisaEntregaObra: i.idItemPesquisaEntregaObra,
          descricao: i.descricao ?? null,
          nota: i.nota ?? null,
        })));
      } else {
        hookForm.reset({ status: 0 });
      }
    }
  }, [
    hookForm,
    open,
    selectedPesquisaSatisfacaoEntrega,
  ]);

  return (
    <>

      <BaseFormControlledModal
        open={open}
        saving={saving}
        loading={loading}
        title='Pesquisa de Satisfação do Cliente'
        onClose={onClose}
        onSubmit={hookForm.handleSubmit(onSubmit)}>
        <FormContainer title="Informações" arrangement="fullRow" gap="small">
          <>

            <EntregaObrasTopHeader
              title=""
              returnButton={false}
              nomeEntregaObra={selectedEntregaObraClienteChecklist != undefined
                ? selectedEntregaObraClienteChecklist.nomeCentroCusto ?? "" : ""}

              nomeChecklist={selectedEntregaObraClienteChecklist != undefined
                ? selectedEntregaObraClienteChecklist.descricaoChecklist ?? "" : ""}

              nomeCliente={selectedEntregaObraClienteChecklist != undefined
                ? selectedEntregaObraClienteChecklist.nomeCliente ?? "" : ""}

              nomeUnidade={selectedEntregaObraClienteChecklist != undefined
                ? selectedEntregaObraClienteChecklist.nomeUnidade ?? "" : ""}

              additionalHeader={
                (
                  <>
                    <div className="w-full mt-2 text-black dark:text-white">
                      <div className="flex flex-row text-black dark:text-white items-center">
                        <span className="font-extrabold text-xl mr-2">Pesquisa: </span>
                        <span className="mx-1 font-base text-base">{selectedPesquisaSatisfacaoEntrega?.descricaoPesquisa ?? ""}</span>
                      </div>
                    </div>
                  </>

                )
              }
              onReload={() => { }}
            />

            <hr className="mt-3 mb-3" />

            <DefaultFormDatePickerInput
              name='data'
              label='Data'
              reactHookForm={hookForm}
            />

            <div className="w-full flex flex-row gap-2 justify-between">
              <DefaultFormRadioGroupInput
                name='status'
                label='Status'
                options={statusOptions}
                reactHookForm={hookForm}
              />

              <div className="flex flex-row gap-2 items-end pb-2 font-bold text-black dark:text-white">
                <span>Total de Pontos:</span>
                <span>
                  {
                    new Intl.NumberFormat("pt-BR", {
                      style: 'percent',
                      minimumFractionDigits: 2,
                    }).format(percPonto)
                  }
                </span>
              </div>
            </div>

            <StyledDataGrid
              tableName="pesquisa_satisfacao_cliente_modal_grid"
              height="auto"
              disableRowSelectionOnClick={true}
              disableColumnMenu={false}
              getRowId={(row: any) => row.idItemPesquisaEntregaObra}
              columns={columnDefinition}
              loading={loading || saving}
              rows={itens ?? []}
              disableColumnSorting={true}
              disableColumnResize={true}
              showToolbar={false}
            />

            <div className="w-full my-4" >
              <DefaultFormInput
                name='observacao'
                label='Observações'
                readOnly={saving}
                multiline={true}
                rows={4}
                reactHookForm={hookForm} />
            </div>

          </>
        </FormContainer>



      </BaseFormControlledModal >
    </>


  );
}